import { SerializedConstructor } from '@langchain/core/load/serializable';
import { ChevronRightIcon, Settings01Icon } from '@langchain/untitled-ui-icons';

import { FC, useEffect, useState } from 'react';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { Text } from '@/design-system';
import { cn } from '@/utils/tailwind';

import {
  useDeep,
  usePlaygroundStore,
} from '../../PlaygroundHome/hooks/usePlaygroundStore';
import {
  MODEL_SETTINGS_MODAL_SUBTITLE,
  MODEL_SETTINGS_MODAL_TITLE,
} from './ModelConfiguration/constants';
import ModelConfigurationSection from './ModelConfigurationSection';
import PromptFormatSection from './PromptFormatSection';
import SettingsModalSection from './SettingsModalSection';
import ToolSettingsSection from './ToolSettingsSection';

enum PromptSettingsModalSection {
  MODEL_CONFIGURATION = 'model_configuration',
  TOOL_SETTINGS = 'tool_settings',
  PROMPT_FORMAT = 'prompt_format',
}

const SECTION_LIST = [
  {
    value: PromptSettingsModalSection.MODEL_CONFIGURATION,
    title: 'Model Configuration',
  },
  {
    value: PromptSettingsModalSection.TOOL_SETTINGS,
    title: 'Tool Settings',
  },
  {
    value: PromptSettingsModalSection.PROMPT_FORMAT,
    title: 'Prompt Format',
  },
];

interface PromptSettingsModalProps {
  index: number;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (isSettingsOpen: boolean) => void;
  currentSavedConfigId: string | undefined;
  setCurrentSavedConfigId: (currentSavedConfigId: string | undefined) => void;
}

interface EvaluatorPromptSettingsModalProps {
  manifest: SerializedConstructor;
  setManifest: (manifest: SerializedConstructor) => void;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (isSettingsOpen: boolean) => void;
  currentSavedConfigId: string | undefined;
  setCurrentSavedConfigId: (currentSavedConfigId: string | undefined) => void;
}

const PromptSettingsModal: FC<PromptSettingsModalProps> = ({
  index,
  isSettingsOpen,
  setIsSettingsOpen,
  currentSavedConfigId,
  setCurrentSavedConfigId,
}) => {
  const [didUpdate, setDidUpdate] = useState(false);

  useEffect(() => {
    if (isSettingsOpen) {
      setDidUpdate(false);
    }
  }, [isSettingsOpen]);

  const handleSettingsChange = () => {
    setDidUpdate(true);
  };

  const [selectedSection, setSelectedSection] =
    useState<PromptSettingsModalSection>(
      PromptSettingsModalSection.MODEL_CONFIGURATION
    );

  const manifest = usePlaygroundStore(
    useDeep((state) => state.prompts[index].manifest)
  );

  const setManifest = usePlaygroundStore((state) => state.setManifest).bind(
    null,
    index
  );

  const setEdited = usePlaygroundStore((state) => state.setEdited).bind(
    null,
    index
  );
  const options = usePlaygroundStore(
    useDeep((state) => state.prompts[index].options)
  );
  const setOptions = usePlaygroundStore((state) => state.setOptions).bind(
    null,
    index
  );

  const renderTitle = () => {
    return (
      <div className="flex flex-row items-center gap-2">
        <div className="rounded-md border border-secondary p-1">
          <Settings01Icon className="h-5 w-5 [&_path]:stroke-[1.5px]" />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row items-center gap-2 text-lg font-semibold">
            <Text variant="h2">Prompt Settings</Text>
            {didUpdate && (
              <Text variant="xs" className="text-tertiary">
                Updated
              </Text>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSectionList = () => {
    return (
      <div className="flex flex-col">
        {SECTION_LIST.map((section) => {
          return (
            <button
              type="button"
              key={section.value}
              className={cn(
                'w-full hover:bg-secondary-hover',
                selectedSection === section.value && 'bg-secondary-hover'
              )}
              onClick={() => setSelectedSection(section.value)}
            >
              <div
                className="flex flex-row items-center justify-between border-b border-secondary p-4 text-left"
                onClick={() => setSelectedSection(section.value)}
              >
                <div className="text-md font-medium">{section.title}</div>
                <ChevronRightIcon className="h-5 w-5" />
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderBody = () => {
    switch (selectedSection) {
      case PromptSettingsModalSection.MODEL_CONFIGURATION:
        return (
          <ModelConfigurationSection
            manifest={manifest}
            setManifest={setManifest}
            setEdited={setEdited}
            options={options}
            setOptions={setOptions}
            currentSavedConfigId={currentSavedConfigId}
            setCurrentSavedConfigId={setCurrentSavedConfigId}
            onSettingsChange={handleSettingsChange}
          />
        );
      case PromptSettingsModalSection.TOOL_SETTINGS:
        return (
          <ToolSettingsSection
            manifest={manifest}
            setManifest={setManifest}
            setEdited={setEdited}
            options={options}
            setOptions={setOptions}
            onSettingsChange={handleSettingsChange}
          />
        );
      case PromptSettingsModalSection.PROMPT_FORMAT:
        return (
          <PromptFormatSection
            manifest={manifest}
            setManifest={setManifest}
            setEdited={setEdited}
            onSettingsChange={handleSettingsChange}
          />
        );
    }
  };

  return (
    <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
      <DialogContent
        title={renderTitle()}
        className="w-[900px] overflow-y-hidden"
        childrenClassName="p-0 h-[75vh]"
      >
        <div className="grid h-full grid-cols-[1fr,3fr] overflow-y-hidden">
          <div className="h-full overflow-y-auto">{renderSectionList()}</div>
          <div className="h-full overflow-y-auto border-l border-secondary">
            <div className="px-8 py-4">{renderBody()}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EvaluatorPromptSettingsModal: FC<EvaluatorPromptSettingsModalProps> = ({
  manifest,
  setManifest,
  isSettingsOpen,
  setIsSettingsOpen,
  currentSavedConfigId,
  setCurrentSavedConfigId,
}) => {
  return (
    <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
      <DialogContent
        title={
          <SettingsModalSection
            title={MODEL_SETTINGS_MODAL_TITLE}
            subtitle={MODEL_SETTINGS_MODAL_SUBTITLE}
          />
        }
        className="h-[500px] w-[800px] overflow-y-scroll"
      >
        <ModelConfigurationSection
          manifest={manifest}
          setManifest={setManifest}
          currentSavedConfigId={currentSavedConfigId}
          setCurrentSavedConfigId={setCurrentSavedConfigId}
          onSettingsChange={() => void 0}
          setEdited={() => void 0}
          hideTitle={true}
        />
      </DialogContent>
    </Dialog>
  );
};

export { EvaluatorPromptSettingsModal, PromptSettingsModal };
